import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import * as StoreService from '../../api/storeService';
import * as StampCardService from '../../api/stampCardService';
import * as LoyaltyRewardService from '../../api/loyaltyRewardsService';

import useNavContext from '../../hooks/useNavContext';

import Spinner from '../../components/Spinner';
import ErrorDialog from '../../components/Dialogs/ErrorDialog';
import AddStampDialog from '../../components/Dialogs/AddStampDialog';
import AddStampSuccessDialog from '../../components/Dialogs/AddStampSuccessDialog';
import useStampCardContext from '../../hooks/useStampCardContext';

interface RouteParams {
  contactlessCode: string;
}

/**
 * Merchant
 */
export default function RedeemContactlessScreen() {
  const { contactlessCode } = useParams<RouteParams>();
  const [isLoading, setIsLoading] = useState(true);
  const [launchAlertDialog, setLaunchAlertDialog] = useState(false);
  const [stampSuccessDialog, setStampSuccessDialog] = useState(false);
  const [launchAddStampDialog, setLaunchAddStampDialog] = useState(false);
  const [stampCard, setStampCard] = useState<StampCardService.StampCard | null>(
    null
  );
  const [merchantId, setMerchantId] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [addedStamp, setAddedStamp] = useState(1);
  const [code, setCode] = useState(contactlessCode);
  const [milestoneThresholdList, setMilestoneThresholdList] = useState<
    number[]
  >([]);
  const nav = useNavContext();
  nav?.removeNav();

  const history = useHistory();
  const sc = useStampCardContext();

  useEffect(() => {
    let subscribed = true;

    const redeem = async () => {
      if (subscribed) {
        const resStore = await StoreService.getStoreByContactlessCode(
          code.split('_')[0]
        );

        setMerchantId(resStore.merchant.id);
        try {
          const resRedeem = await StampCardService.creditStampCardContactless(
            code
          );
          if (resRedeem) {
            const resMilestone = await LoyaltyRewardService.listMilestone(
              resRedeem.loyalty_campaign.id
            );

            if (resMilestone && resMilestone.length > 0) {
              setMilestoneThresholdList(resMilestone.map((m) => m.threshold));
            }
            console.log('ResRedeem: ', resRedeem);
            setStampCard(resRedeem);
            setIsLoading(false);
            setStampSuccessDialog(true);
            setCode('');
          }
        } catch (e: any) {
          if (e && e.data && e.data[0].detail) {
            if (e.data[0].detail === 'Stamp count exceeds store limit') {
              setErrorMsg(
                `You've reached the max number of stamps you can collect from this location.`
              );
            } else {
              setErrorMsg(e.data[0].detail);
            }
          } else {
            setErrorMsg(
              'To collect more than 1 stamp, please pass your device to a staff member.'
            );
          }

          setIsLoading(false);
          setLaunchAlertDialog(true);
          setCode('');
          // }
        }
      }
    };

    if (isLoading && code.length > 0) {
      redeem();
    }

    return () => {
      subscribed = false;
      setCode('');
      setIsLoading(false);
    };
  }, [isLoading, code]);

  const handleClose = async (
    stampCardId: string,
    slotNumber: number,
    stampCampaignId: string,
    status: string
  ) => {
    // check if stampcard completed, get new stampCard id
    if (status !== 'completed') {
      if (sc) {
        sc.setStampCard({
          selectedStampCardId: stampCardId,
          selectedSlotNumber: slotNumber,
          selectedStampCampaignId: stampCampaignId,
        });
      }
      setStampSuccessDialog(false);
      history.replace(`/merchant/${merchantId}`);
      window.location.reload();
    } else {
      try {
        const resNewStampCard =
          await StampCardService.getStampCardByCampaignAndSlot(
            stampCampaignId,
            slotNumber
          );

        if (resNewStampCard.id !== stampCardId) {
          if (sc) {
            sc.setStampCard({
              selectedStampCardId: resNewStampCard.id,
              selectedSlotNumber: resNewStampCard.slotNumber,
              selectedStampCampaignId: resNewStampCard.loyalty_campaign.id,
            });
          }
          setStampSuccessDialog(false);
          history.replace(`/merchant/${merchantId}`);
          window.location.reload();
        } else {
          if (sc) {
            sc.setStampCard({
              selectedStampCardId: stampCardId,
              selectedSlotNumber: slotNumber,
              selectedStampCampaignId: stampCampaignId,
            });
          }
          setStampSuccessDialog(false);
          history.replace(`/merchant/${merchantId}`);
          window.location.reload();
        }
      } catch (e) {
        console.log('Error: Fetch Stampcard from merchant');
        if (sc) {
          sc.setStampCard({
            selectedStampCardId: stampCardId,
            selectedSlotNumber: slotNumber,
            selectedStampCampaignId: stampCampaignId,
          });
        }
        setStampSuccessDialog(false);
        history.replace(`/merchant/${merchantId}`);
        window.location.reload();
      }
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 200,
      }}
    >
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          {merchantId && (
            <>
              <ErrorDialog
                open={launchAlertDialog}
                onClose={() => {
                  setLaunchAlertDialog(false);
                  history.replace(`/merchant/${merchantId}`);
                  window.location.reload();
                }}
                onConfirm={() => {
                  setLaunchAlertDialog(false);
                  history.replace(`/merchant/${merchantId}`);
                  window.location.reload();
                }}
                title="Error"
                body={errorMsg}
                confirmButtonText="OK"
              />
              {stampCard && merchantId && (
                <>
                  <AddStampDialog
                    stampCardId={stampCard.id}
                    open={launchAddStampDialog}
                    onClose={() => {
                      setLaunchAddStampDialog(false);
                      history.replace(`/merchant/${merchantId}`);
                      window.location.reload();
                    }}
                    confirmRedeem={(addedStamp: number) => {
                      setAddedStamp(addedStamp);
                      setLaunchAddStampDialog(false);
                      setStampSuccessDialog(true);
                    }}
                  />
                  <AddStampSuccessDialog
                    isComplete={stampCard.quantity === stampCard.redemptionCost}
                    isMilestone={
                      milestoneThresholdList.length > 0
                        ? milestoneThresholdList.includes(stampCard.quantity)
                        : false
                    }
                    open={stampSuccessDialog}
                    onClose={() => {
                      handleClose(
                        stampCard.id,
                        stampCard.slotNumber,
                        stampCard.loyalty_campaign.id,
                        stampCard.status
                      );
                    }}
                    campaignId={stampCard.loyalty_campaign.id}
                    quantity={stampCard.quantity}
                    isMultipleStamp={addedStamp > 1}
                    stampAdded={addedStamp.toString()}
                    allowAddMoreStamp={true}
                    handleAddMoreStamp={() => {
                      if (sc) {
                        sc.setStampCard({
                          selectedStampCardId: stampCard.id,
                          selectedSlotNumber: stampCard.slotNumber,
                          selectedStampCampaignId:
                            stampCard.loyalty_campaign.id,
                        });
                      }
                      setStampSuccessDialog(false);
                      setLaunchAddStampDialog(true);
                    }}
                  />
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
